import type { MockBridge } from "./bridge";
import type { DebugMenu } from "./menu";

/**
 * The mock bridge and debug menu are only needed when a game runs outside the
 * Jest platform (local development / preview). They account for ~40% of the
 * bundle, so they are split out and loaded on demand instead of shipped to
 * every player.
 */
export interface MockDeps {
  MockBridge: typeof MockBridge;
  DebugMenu: typeof DebugMenu;
}

declare global {
  interface Window {
    __JESTSDK_MOCK__?: MockDeps;
  }
}

/**
 * Defined at build time. `true` in the IIFE/CDN build (which cannot be code
 * split, so the mock bundle is fetched at runtime as a sibling script),
 * `false` in the ESM build (where a dynamic import lets the consumer's bundler
 * emit a lazy chunk). Undefined under tests, where the ESM path is used.
 */
declare const __JESTSDK_IIFE__: boolean;

const IS_IIFE_BUILD =
  typeof __JESTSDK_IIFE__ !== "undefined" && __JESTSDK_IIFE__;

// Captured during the initial synchronous execution of the IIFE, while
// `document.currentScript` still points at the loading `<script>` tag, so the
// mock bundle can be resolved relative to wherever the SDK was served from.
const SDK_SCRIPT_SRC =
  typeof document !== "undefined"
    ? (document.currentScript as HTMLScriptElement | null)?.src
    : undefined;

export async function loadMockDeps(): Promise<MockDeps> {
  if (IS_IIFE_BUILD) {
    return loadMockBundleFromScript();
  }

  const [bridgeModule, menuModule] = await Promise.all([
    import("./bridge"),
    import("./menu"),
  ]);
  return {
    MockBridge: bridgeModule.MockBridge,
    DebugMenu: menuModule.DebugMenu,
  };
}

function mockBundleUrl(): string {
  if (SDK_SCRIPT_SRC) {
    return new URL("jestsdk-mock.js", SDK_SCRIPT_SRC).href;
  }
  return "jestsdk-mock.js";
}

let mockBundlePromise: Promise<MockDeps> | undefined;

function loadMockBundleFromScript(): Promise<MockDeps> {
  if (typeof document === "undefined") {
    return Promise.reject(
      new Error("JestSDK mock mode requires a browser environment"),
    );
  }
  if (window.__JESTSDK_MOCK__) {
    return Promise.resolve(window.__JESTSDK_MOCK__);
  }
  // Dedupe concurrent loads so the bundle is fetched and injected only once.
  if (mockBundlePromise) {
    return mockBundlePromise;
  }

  mockBundlePromise = new Promise<MockDeps>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = mockBundleUrl();
    script.async = true;
    script.onload = () => {
      const deps = window.__JESTSDK_MOCK__;
      if (deps) {
        resolve(deps);
      } else {
        reject(new Error("jestsdk-mock.js loaded but did not register"));
      }
    };
    script.onerror = () => reject(new Error("Failed to load jestsdk-mock.js"));
    document.head.appendChild(script);
  });
  // Clear on failure so a later call can retry the fetch.
  mockBundlePromise.catch(() => {
    mockBundlePromise = undefined;
  });
  return mockBundlePromise;
}
